"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createMinMaxResolution = createMinMaxResolution;
exports.createSnapToPower = createSnapToPower;
exports.createSnapToResolutions = createSnapToResolutions;

var _math = require("./math.js");

var _extent = require("./extent.js");

var _array = require("./array.js");

/**
 * @module ol/resolutionconstraint
 */

/**
 * @typedef {function((number|undefined), number, import("./size.js").Size, boolean=): (number|undefined)} Type
 */

/**
 * Returns a modified resolution taking into account the viewport size and maximum
 * allowed extent.
 * @param {number} resolution Resolution
 * @param {import("./extent.js").Extent} maxExtent Maximum allowed extent.
 * @param {import("./size.js").Size} viewportSize Viewport size.
 * @param {boolean} showFullExtent Whether to show the full extent.
 * @return {number} Capped resolution.
 */
function getViewportClampedResolution(resolution, maxExtent, viewportSize, showFullExtent) {
  var xResolution = (0, _extent.getWidth)(maxExtent) / viewportSize[0];
  var yResolution = (0, _extent.getHeight)(maxExtent) / viewportSize[1];

  if (showFullExtent) {
    return Math.min(resolution, Math.max(xResolution, yResolution));
  }

  return Math.min(resolution, Math.min(xResolution, yResolution));
}
/**
 * Returns a modified resolution to be between maxResolution and minResolution while
 * still allowing the value to be slightly out of bounds.
 * Note: the computation is based on the logarithm function (ln):
 *  - at 1, ln(x) is 0
 *  - above 1, ln(x) keeps increasing but at a much slower pace than x
 * The final result is clamped to prevent getting too far away from bounds.
 * @param {number} resolution Resolution.
 * @param {number} maxResolution Max resolution.
 * @param {number} minResolution Min resolution.
 * @return {number} Smoothed resolution.
 */


function getSmoothClampedResolution(resolution, maxResolution, minResolution) {
  var result = Math.min(resolution, maxResolution);
  var ratio = 50;
  result *= Math.log(1 + ratio * Math.max(0, resolution / maxResolution - 1)) / ratio + 1;

  if (minResolution) {
    result = Math.max(result, minResolution);
    result /= Math.log(1 + ratio * Math.max(0, minResolution / resolution - 1)) / ratio + 1;
  }

  return (0, _math.clamp)(result, minResolution / 2, maxResolution * 2);
}
/**
 * @param {Array<number>} resolutions Resolutions.
 * @param {boolean} [opt_smooth] If true, the view will be able to slightly exceed resolution limits. Default: true.
 * @param {import("./extent.js").Extent} [opt_maxExtent] Maximum allowed extent.
 * @param {boolean} [opt_showFullExtent] If true, allows us to show the full extent. Default: false.
 * @return {Type} Zoom function.
 */


function createSnapToResolutions(resolutions, opt_smooth, opt_maxExtent, opt_showFullExtent) {
  return (
    /**
     * @param {number|undefined} resolution Resolution.
     * @param {number} direction Direction.
     * @param {import("./size.js").Size} size Viewport size.
     * @param {boolean} [opt_isMoving] True if an interaction or animation is in progress.
     * @return {number|undefined} Resolution.
     */
    function (resolution, direction, size, opt_isMoving) {
      if (resolution !== undefined) {
        var maxResolution = resolutions[0];
        var minResolution = resolutions[resolutions.length - 1];
        var cappedMaxRes = opt_maxExtent ? getViewportClampedResolution(maxResolution, opt_maxExtent, size, opt_showFullExtent) : maxResolution; // during interacting or animating, allow intermediary values

        if (opt_isMoving) {
          var smooth = opt_smooth !== undefined ? opt_smooth : true;

          if (!smooth) {
            return (0, _math.clamp)(resolution, minResolution, cappedMaxRes);
          }

          return getSmoothClampedResolution(resolution, cappedMaxRes, minResolution);
        }

        var capped = Math.min(cappedMaxRes, resolution);
        var z = Math.floor((0, _array.linearFindNearest)(resolutions, capped, direction));

        if (resolutions[z] > cappedMaxRes && z < resolutions.length - 1) {
          return resolutions[z + 1];
        }

        return resolutions[z];
      } else {
        return undefined;
      }
    }
  );
}
/**
 * @param {number} power Power.
 * @param {number} maxResolution Maximum resolution.
 * @param {number} [opt_minResolution] Minimum resolution.
 * @param {boolean} [opt_smooth] If true, the view will be able to slightly exceed resolution limits. Default: true.
 * @param {import("./extent.js").Extent} [opt_maxExtent] Maximum allowed extent.
 * @param {boolean} [opt_showFullExtent] If true, allows us to show the full extent. Default: false.
 * @return {Type} Zoom function.
 */


function createSnapToPower(power, maxResolution, opt_minResolution, opt_smooth, opt_maxExtent, opt_showFullExtent) {
  return (
    /**
     * @param {number|undefined} resolution Resolution.
     * @param {number} direction Direction.
     * @param {import("./size.js").Size} size Viewport size.
     * @param {boolean} [opt_isMoving] True if an interaction or animation is in progress.
     * @return {number|undefined} Resolution.
     */
    function (resolution, direction, size, opt_isMoving) {
      if (resolution !== undefined) {
        var cappedMaxRes = opt_maxExtent ? getViewportClampedResolution(maxResolution, opt_maxExtent, size, opt_showFullExtent) : maxResolution;
        var minResolution = opt_minResolution !== undefined ? opt_minResolution : 0; // during interacting or animating, allow intermediary values

        if (opt_isMoving) {
          var smooth = opt_smooth !== undefined ? opt_smooth : true;

          if (!smooth) {
            return (0, _math.clamp)(resolution, minResolution, cappedMaxRes);
          }

          return getSmoothClampedResolution(resolution, cappedMaxRes, minResolution);
        }

        var tolerance = 1e-9;
        var minZoomLevel = Math.ceil(Math.log(maxResolution / cappedMaxRes) / Math.log(power) - tolerance);
        var offset = -direction * (0.5 - tolerance) + 0.5;
        var capped = Math.min(cappedMaxRes, resolution);
        var cappedZoomLevel = Math.floor(Math.log(maxResolution / capped) / Math.log(power) + offset);
        var zoomLevel = Math.max(minZoomLevel, cappedZoomLevel);
        var newResolution = maxResolution / Math.pow(power, zoomLevel);
        return (0, _math.clamp)(newResolution, minResolution, cappedMaxRes);
      } else {
        return undefined;
      }
    }
  );
}
/**
 * @param {number} maxResolution Max resolution.
 * @param {number} minResolution Min resolution.
 * @param {boolean} [opt_smooth] If true, the view will be able to slightly exceed resolution limits. Default: true.
 * @param {import("./extent.js").Extent} [opt_maxExtent] Maximum allowed extent.
 * @param {boolean} [opt_showFullExtent] If true, allows us to show the full extent. Default: false.
 * @return {Type} Zoom function.
 */


function createMinMaxResolution(maxResolution, minResolution, opt_smooth, opt_maxExtent, opt_showFullExtent) {
  return (
    /**
     * @param {number|undefined} resolution Resolution.
     * @param {number} direction Direction.
     * @param {import("./size.js").Size} size Viewport size.
     * @param {boolean} [opt_isMoving] True if an interaction or animation is in progress.
     * @return {number|undefined} Resolution.
     */
    function (resolution, direction, size, opt_isMoving) {
      if (resolution !== undefined) {
        var cappedMaxRes = opt_maxExtent ? getViewportClampedResolution(maxResolution, opt_maxExtent, size, opt_showFullExtent) : maxResolution;
        var smooth = opt_smooth !== undefined ? opt_smooth : true;

        if (!smooth || !opt_isMoving) {
          return (0, _math.clamp)(resolution, minResolution, cappedMaxRes);
        }

        return getSmoothClampedResolution(resolution, cappedMaxRes, minResolution);
      } else {
        return undefined;
      }
    }
  );
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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